/**
 * #109 · Verificación del archivo antes de mandárselo al proveedor.
 *
 * Existe porque "quiero ver el archivo para validarlo" no se resuelve
 * mostrándolo: está cifrado, y aunque se descifre, leer líneas a ojo no
 * responde la pregunta que importa —si lo que se manda a imprimir produce
 * exactamente las unidades que quedaron en la base—. Eso se comprueba, y es
 * lo que hace el endpoint: descifra en memoria, valida el tag, compara la
 * cabecera con la orden, parsea cada código y hashea cada secreto contra
 * `unit.secret_hash`.
 *
 * Lo que esta pantalla tiene que dejar claro:
 *
 * 1. **El veredicto primero.** Verde o rojo arriba de todo, y los problemas en
 *    palabras. Los números están abajo para quien quiera mirarlos.
 * 2. **Los secretos van enmascarados** y no completos: con el código bajo
 *    raspado se reclama la unidad como si se la tuviera en la mano. Los cuatro
 *    primeros caracteres alcanzan para cotejar una fila contra la muestra que
 *    mande el proveedor.
 * 3. **Muestreo vs. completo.** Por defecto se comprueban 2.000 registros —los
 *    1.000 primeros y los 1.000 últimos, que es donde aparece un error de
 *    generación por bloques—. `full` comprueba todos y tarda proporcionalmente:
 *    la diferencia se dice, no se esconde.
 */
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { labelOrderKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { DetailList, DetailRow } from '../products/ui/fields';

type Veredicto = Schemas['PrintFileVerifyOut'];

export function VerifyPanel({ orderId }: { orderId: number }): ReactNode {
  const { t, fmt } = useI18n();
  const [pedido, setPedido] = useState(false);
  const [completo, setCompleto] = useState(false);

  // `staleTime: 0`: el veredicto es sobre el objeto que está AHORA en el
  // store. Servir uno cacheado sería afirmar algo sobre un archivo que pudo
  // cambiar, que es justo lo que esto viene a descartar.
  const verify = useApiQuery<Veredicto>(
    labelOrderKeys.verify(orderId, completo),
    `/api/v1/label-orders/${String(orderId)}/print-file/verify`,
    completo ? { full: true } : undefined,
    { enabled: pedido, staleTime: 0 },
  );

  if (!pedido) {
    return (
      <div className="space-y-2">
        <Button
          variant="secondary"
          onClick={() => {
            setPedido(true);
          }}
        >
          {t('labels.verify.request')}
        </Button>
        <p className="text-xs text-ink-subtle">{t('labels.verify.request.hint')}</p>
      </div>
    );
  }

  if (verify.isPending) return <LoadingState rows={3} />;
  if (verify.isError) {
    return (
      <ErrorState
        error={verify.error}
        onRetry={() => {
          void verify.refetch();
        }}
      />
    );
  }

  const data = verify.data;
  // Los dos campos tienen `default_factory` del lado de Python, así que la
  // API siempre los manda; el OpenAPI los marca opcionales porque tienen
  // default. Se normalizan acá y no con `!`: una lista vacía es un estado
  // legítimo —un archivo sin problemas— y tratarlo como imposible es cómo se
  // rompe una pantalla en producción.
  const problemas = data.problemas ?? [];
  const preview = data.preview ?? [];

  return (
    <div className="space-y-3">
      <div
        role="status"
        className={
          data.ok
            ? 'rounded-(--radius-card) border border-border bg-surface-sunken p-4'
            : 'rounded-(--radius-card) border border-critical/30 bg-critical-soft p-4'
        }
      >
        <p className="text-sm font-semibold text-ink">
          {t(data.ok ? 'labels.verify.ok' : 'labels.verify.fail')}
        </p>
        {problemas.length > 0 && (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink">
            {problemas.map((problema) => (
              <li key={problema}>{problema}</li>
            ))}
          </ul>
        )}
      </div>

      <DetailList>
        <DetailRow
          label={t('labels.verify.records')}
          value={`${fmt.number(data.records)} / ${fmt.number(data.records_esperados)}`}
        />
        <DetailRow
          label={t('labels.verify.checked')}
          value={
            <span className="flex items-center gap-2">
              {fmt.number(data.verificados)}
              {data.muestreo && <Badge tone="neutral">{t('labels.verify.sampled')}</Badge>}
            </span>
          }
        />
        <DetailRow
          label={t('labels.verify.codes')}
          value={fmt.number(data.codigos_invalidos)}
        />
        <DetailRow
          label={t('labels.verify.secrets')}
          value={fmt.number(data.secretos_no_coinciden)}
        />
        <DetailRow
          label={t('labels.verify.sha256')}
          value={t(data.sha256_ok ? 'labels.verify.sha256.ok' : 'labels.verify.sha256.fail')}
        />
      </DetailList>

      {preview.length > 0 && (
        <div className="overflow-x-auto">
          <Table caption={t('labels.verify.preview')}>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>{t('labels.verify.col.roll')}</Th>
                <Th>{t('labels.verify.col.code')}</Th>
                <Th>{t('labels.verify.col.secret')}</Th>
              </tr>
            </thead>
            <tbody>
              {preview.map((fila) => (
                <tr key={fila.seq}>
                  <Td className="tabular-nums">{fila.seq}</Td>
                  <Td className="font-mono text-xs">{fila.roll_code}</Td>
                  <Td className="font-mono text-xs">{fila.code}</Td>
                  <Td className="font-mono text-xs text-ink-muted">{fila.secret_masked}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="mt-1 text-xs text-ink-subtle">{t('labels.verify.masked')}</p>
        </div>
      )}

      {!completo && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setCompleto(true);
          }}
        >
          {t('labels.verify.full')}
        </Button>
      )}
    </div>
  );
}
